import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react'
import { cn } from '../../lib/cn'

export interface ComposerFieldProps extends ComponentPropsWithoutRef<'textarea'> {
  value: string
  /**
   * Painted behind the textarea at identical metrics, for a caller that draws
   * its own text (the Agent paints `@`-mention chips over the tokens). Supply
   * it only with `transparentText`, or the two texts stack.
   */
  overlay?: ReactNode
  /** Hides the textarea's own glyphs so `overlay` is what the reader sees. */
  transparentText?: boolean
  /** Rendered inside the scroller, after the field: caret-anchored menus. */
  children?: ReactNode
  /**
   * Height cap in pixels before the field scrolls its own text. Ignored while
   * an `overlay` is mounted: the overlay is pinned to the textarea's box, so
   * the textarea has to keep its full height and the scroller does the
   * scrolling — capping the textarea would leave the two drifting apart.
   */
  maxHeight?: number
  /** Layout for the scroll container (a height cap, `flex-1`); text chrome is the field's. */
  className?: string
  textareaClassName?: string
}

/**
 * The composer's text surface: a textarea that grows to its content inside a
 * capped scroller, with an optional mirror overlay at the same metrics.
 *
 * The metrics live in one place (`.composer-field`) because the overlay only
 * works while it wraps identically to the textarea beneath it — two copies of
 * the same font size and line height is how that silently drifts.
 */
export const ComposerField = forwardRef<HTMLTextAreaElement, ComposerFieldProps>(
  function ComposerField(
    {
      overlay,
      transparentText = false,
      children,
      maxHeight = 200,
      className,
      textareaClassName,
      value,
      rows = 1,
      ...props
    },
    ref
  ) {
    const mirrored = overlay != null
    const scrollerRef = useRef<HTMLDivElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement, [])

    /**
     * Grow to the full content height; the scroller caps what is visible.
     *
     * The scroller's box is locked while the textarea collapses to `auto` for
     * measurement: that transient layout otherwise grows whatever the composer
     * sits in, and a bottom-pinned transcript above it gets clamped upward on
     * every multi-line edit.
     */
    const autosize = useCallback(() => {
      const textarea = textareaRef.current
      if (!textarea) return
      const scroller = scrollerRef.current
      if (scroller) scroller.style.height = `${scroller.offsetHeight}px`
      textarea.style.height = 'auto'
      const full = textarea.scrollHeight
      const cap = mirrored ? full : Math.min(full, maxHeight)
      textarea.style.height = `${cap}px`
      textarea.style.overflowY = full > cap ? 'auto' : 'hidden'
      if (scroller) scroller.style.height = ''
    }, [maxHeight, mirrored])

    useLayoutEffect(autosize, [value, autosize])

    /**
     * The textarea carries an inline pixel height, so a width change (window
     * resize, sidebar toggle) rewraps the text taller while the box stays at
     * its old height — and an overlay then paints over the scroller with no
     * textarea beneath it. Only width is compared: autosize writes the height,
     * which would re-notify this observer forever.
     */
    useEffect(() => {
      const scroller = scrollerRef.current
      if (!scroller) return
      let lastWidth: number | null = null
      const observer = new ResizeObserver(([entry]) => {
        const width = entry.contentRect.width
        if (width === lastWidth) return
        lastWidth = width
        autosize()
      })
      observer.observe(scroller)
      return () => observer.disconnect()
    }, [autosize])

    /** A click on the padding or the space under the last line places the caret. */
    const focusFromSurface = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === textareaRef.current) return
      if ((e.target as HTMLElement).closest('button')) return
      textareaRef.current?.focus()
    }, [])

    return (
      <div ref={scrollerRef} className={cn('composer-field', className)} onClick={focusFromSurface}>
        <div className='composer-field__sizer'>
          {overlay ? (
            <div className='composer-field__text composer-field__overlay' aria-hidden='true'>
              {overlay}
            </div>
          ) : null}
          <textarea
            {...props}
            ref={textareaRef}
            value={value}
            rows={rows}
            className={cn(
              'composer-field__text composer-field__input',
              transparentText && 'composer-field__input--mirrored',
              textareaClassName
            )}
          />
        </div>
        {children}
      </div>
    )
  }
)
